import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  appointmentArrivalSchema,
  TAppointmentArrivalForm,
} from "./appointmentArrivalSchema";
import {
  createTicketAutRecRuleArrivalRule,
  getTicketAutRecRuleArrivalRuleById,
  updateTicketAutRecRuleArrivalRuleById,
} from "../../../../../../../services/ticketAutRecRuleArrivalRule.service";
import { IAppointmentArrivalProps } from "./models";
import { SelectEstablishments } from "./SelectEstablishments";
import { Procedures } from "./Procedures";
import { Calendars } from "./Calendars";
import { Classifications } from "./Classifications";
import { Insurances } from "./Insurances";
import { appointmentArrivalList } from "../../../../../../../services/appointmentArrival.service";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.appointmentArrival.";

export const AppointmentArrival = memo(
  ({
    id,
    type,
    ticketAutomationReceivedRuleId,
    onBackButton,
  }: IAppointmentArrivalProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutRecRuleArrivalRule,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutRecRuleArrivalRuleById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutRecRuleArrivalRuleById,
    );

    const saveHandler = (v: TAppointmentArrivalForm) =>
      save({
        ...v,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        tasyScheduleTypeCode: type === "consultation" ? "3" : "2",
        appointmentArrivalRuleId: v.appointmentArrivalRule?.id,
        calendars: v.calendars?.map((calendar) => ({
          externalId: calendar.id,
          externalName: calendar.name,
        })),
        classifications: v.classifications?.map((calendar) => ({
          externalId: calendar.id,
          externalName: calendar.name,
        })),
        establishments: v.establishments?.map((calendar) => ({
          externalId: calendar.id,
          externalName: calendar.name,
        })),
        insurances: v.insurances?.map((calendar) => ({
          externalId: calendar.id,
          externalName: calendar.name,
        })),
        procedures: v.procedures?.map((calendar) => ({
          externalId: calendar.id,
          externalName: calendar.name,
        })),
      });

    const updateHandler = (v: TAppointmentArrivalForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
          tasyScheduleTypeCode: type === "consultation" ? "3" : "2",
          appointmentArrivalRuleId: v.appointmentArrivalRule?.id,
          calendars: v.calendars?.map((calendar) => ({
            externalId: calendar.id,
            externalName: calendar.name,
          })),
          classifications: v.classifications?.map((calendar) => ({
            externalId: calendar.id,
            externalName: calendar.name,
          })),
          establishments: v.establishments?.map((calendar) => ({
            externalId: calendar.id,
            externalName: calendar.name,
          })),
          insurances: v.insurances?.map((calendar) => ({
            externalId: calendar.id,
            externalName: calendar.name,
          })),
          procedures: v.procedures?.map((calendar) => ({
            externalId: calendar.id,
            externalName: calendar.name,
          })),
        },
        id: id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TAppointmentArrivalForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TAppointmentArrivalForm = {
            ...data,
            calendars: data.ticketAutRecRuleArrivalRuleCalendars?.map(
              (calendar) => ({
                id: calendar.externalId,
                name: calendar.externalName,
              }),
            ),
            procedures: data.ticketAutRecRuleArrivalRuleProcedures?.map(
              (procedure) => ({
                id: procedure.externalId,
                name: procedure.externalName,
              }),
            ),
            classifications:
              data.ticketAutRecRuleArrivalRuleClassifications?.map(
                (classification) => ({
                  id: classification.externalId,
                  name: classification.externalName,
                }),
              ),
            establishments: data.ticketAutRecRuleArrivalRuleEstablishments?.map(
              (establishment) => ({
                id: establishment.externalId,
                name: establishment.externalName,
              }),
            ),
            insurances: data.ticketAutRecRuleArrivalRuleInsurances?.map(
              (insurance) => ({
                id: insurance.externalId,
                name: insurance.externalName,
              }),
            ),
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TAppointmentArrivalForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(
          translationPath +
            (type === "consultation"
              ? "appointment_arrival_consultation"
              : "appointment_arrival_exam"),
        )}
        schema={appointmentArrivalSchema}
        subtitle={t(
          translationPath +
            (type === "consultation"
              ? "new_appointment_arrival_consultation"
              : "new_appointment_arrival_exam"),
        )}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Box sx={{ pl: 2 }}>
            <Grid
              container
              spacing={2}
              sx={(t) => ({
                pr: 3,
                pl: 1,
                py: 3,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 2,
              })}
            >
              <Grid item sm={12}>
                <Typography fontWeight="bold">
                  {t(translationPath + "if")}
                </Typography>
              </Grid>

              <SelectEstablishments />

              <Insurances />

              <Calendars type={type} />

              {type === "consultation" && <Classifications />}

              {type === "exam" && <Procedures />}
            </Grid>

            <Grid
              container
              spacing={2}
              sx={(t) => ({
                mt: 5,
                pr: 3,
                pl: 1,
                py: 3,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 2,
              })}
            >
              <Grid item sm={12}>
                <Typography fontWeight="bold">
                  {t(translationPath + "then")}
                </Typography>
              </Grid>

              <Grid item sm={12}>
                <AutoComplete
                  name="appointmentArrivalRule"
                  size="small"
                  request={appointmentArrivalList}
                  label={t(translationPath + "appointment_arrival_rule")}
                  getOptionLabel={(option) => option.name}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}
      />
    );
  },
);
